import React from 'react';

export interface Column<T> {
  key: string;
  header: string;
  width?: string; // Ancho personalizado (e.g., 'w-1/4', 'w-48', 'min-w-[200px]')
  minWidth?: string; // Ancho mínimo (e.g., 'min-w-[150px]')
  maxWidth?: string; // Ancho máximo (e.g., 'max-w-[300px]')
  render?: (item: T, index: number) => React.ReactNode;
  className?: string; // Clases adicionales para la celda
  headerClassName?: string; // Clases adicionales para el header
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  className?: string;
  tableClassName?: string;
  headerClassName?: string;
  rowClassName?: string | ((item: T, index: number) => string);
  emptyMessage?: string;
  loading?: boolean;
  loadingMessage?: string;
  mobileCard?: boolean;
}

export const DataTable = <T,>({
  data,
  columns,
  className = '',
  tableClassName = '',
  headerClassName = '',
  rowClassName = '',
  emptyMessage = 'No hay datos disponibles',
  loading = false,
  loadingMessage = 'Cargando...',
  mobileCard = false,
}: DataTableProps<T>) => {
  const getColumnClasses = (column: Column<T>) => {
    const classes = [];
    
    if (column.width) classes.push(column.width);
    if (column.minWidth) classes.push(column.minWidth);
    if (column.maxWidth) classes.push(column.maxWidth);
    
    return classes.join(' ');
  };

  const getRowClassName = (item: T, index: number) => {
    if (typeof rowClassName === 'function') {
      return rowClassName(item, index);
    }
    return rowClassName;
  };

  if (loading) {
    return (
      <div className={`overflow-hidden bg-white rounded-lg shadow-sm ${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-600 border-solid rounded-full animate-spin border-t-transparent"></div>
            <p className="text-gray-500">{loadingMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden bg-white rounded-lg shadow-sm ${className}`}>
      {/* Desktop table (hidden on small screens when mobileCard enabled) */}
      <div className={`${mobileCard ? 'hidden md:block' : ''} overflow-x-auto`}>
        <table className={`w-full divide-y divide-gray-200 ${tableClassName}`}>
          <thead className={`bg-gray-50 ${headerClassName}`}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase ${getColumnClasses(
                    column
                  )} ${column.headerClassName || ''}`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr
                  key={index}
                  className={`hover:bg-gray-50 transition-colors ${getRowClassName(
                    item,
                    index
                  )}`}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-6 py-4 ${getColumnClasses(column)} ${column.className || ''}`}
                    >
                      <div className="truncate">
                        {column.render
                          ? column.render(item, index)
                          : String((item as Record<string, unknown>)[column.key] || '')
                        }
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list (visible on small screens when mobileCard enabled) */}
      {mobileCard && (
        <div className="px-3 py-2 md:hidden">
          {data.length === 0 ? (
            <div className="py-6 text-center text-gray-500">{emptyMessage}</div>
          ) : (
            data.map((item, index) => {
              const primary = columns[0];
              const rest = columns.slice(1);
              const primaryValue = primary.render ? primary.render(item, index) : String((item as Record<string, unknown>)[primary.key] || '');
              return (
                <div
                  key={index}
                  className={`mb-3 p-4 bg-white border border-blue-500 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-150 ${getRowClassName(
                    item,
                    index
                  )} cursor-pointer`}
                >
                  <div className="flex items-start justify-between space-x-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs tracking-wide text-gray-500 uppercase">{primary.header}</div>
                      <div className="mt-1 text-base font-semibold text-gray-900 truncate">{primaryValue}</div>
                    </div>
                    {/* show a small secondary area with first of rest columns if exists */}
                    {rest[0] && (
                      <div className="flex-shrink-0 ml-2 text-sm text-gray-500">
                        {rest[0].render ? rest[0].render(item, index) : String((item as Record<string, unknown>)[rest[0].key] || '')}
                      </div>
                    )}
                  </div>

                  {rest.length > 1 && (
                    <div className="grid grid-cols-2 gap-2 mt-3 text-sm text-gray-600">
                      {rest.slice(1).map((column) => (
                        <div key={column.key} className={`${column.className || ''}`}>
                          <div className="text-xs text-gray-500">{column.header}</div>
                            <div className="mt-0.5 text-sm text-gray-800 truncate">
                              {column.render ? column.render(item, index) : String((item as Record<string, unknown>)[column.key] || '')}
                            </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      {!mobileCard && data.length === 0 && (
        // if not mobileCard and empty, we've already shown empty above; keep consistent
        null
      )}
    </div>
  );
};

export default DataTable;
